/**
 * Vendor quote → proposal: pure change-building and comparison logic (mockup F–H, rules R2/R3).
 *
 * API-SWAP: once `POST proposal-scopes/{id}/apply-vendor-quotes` exists the server builds and applies
 * the plan in one transaction; this module then only renders what the server returns.
 */
import { ApplyPlan, ChangeKind, MaterialSelection, PlannedChange, ProposalLineState, QuoteOption, QuoteSummary } from './vendor-quote-flow.types';

/** Fields that make a line "changed" rather than "same". */
const DIFF_FIELDS: Array<keyof ProposalLineState> = ['qty', 'unitCost', 'vendorName', 'name'];

/** A proposal line as the quote item would set it (vendor, name, description, qty, unit cost, source). Profit % is kept (R3). */
export function lineFromOption(opt: QuoteOption, profitPct: number, base?: ProposalLineState | null, qty?: number): ProposalLineState {
  return {
    id: base?.id ?? null,
    productId: opt.productId,
    tag: base?.tag ?? '',
    name: opt.itemName,
    description: opt.itemDescription,
    vendorId: opt.vendorId,
    vendorName: opt.vendorName,
    qty: qty ?? opt.qty,
    unit: opt.unit || base?.unit || '',
    unitCost: opt.unitPrice,
    profitPct: base?.profitPct ?? profitPct,
    quoteSource: opt.quotationReference,
  };
}

function diffKind(before: ProposalLineState, after: ProposalLineState): ChangeKind {
  return DIFF_FIELDS.some(f => before[f] !== after[f]) ? 'changed' : 'same';
}

/** Plan for one material (F): the first chosen quote replaces the line, every further one adds a line (F3 split). */
export function buildMaterialPlan(line: ProposalLineState, options: QuoteOption[], selection: MaterialSelection, defaultProfitPct: number): ApplyPlan {
  const chosen = [...selection.entries()]
    .map(([key, qty]) => ({ opt: options.find(o => o.optionKey === key)!, qty }))
    .filter(x => !!x.opt);
  const split = chosen.length > 1;
  const changes: PlannedChange[] = chosen.map(({ opt, qty }, k) => {
    const after = lineFromOption(opt, defaultProfitPct, k === 0 ? line : null, split ? qty : opt.qty);
    return k === 0 ? { kind: diffKind(line, after), before: line, after } : { kind: 'added', before: null, after };
  });
  const label = split ? `${chosen.length} quotes on ${line.name}` : `${chosen[0]?.opt.quotationReference ?? ''} on ${line.name}`;
  return { from: 'material', label, changes };
}

/** Plan for a whole scope (G/H): every line gets the lowest quoted price (R2); quoted products the scope lacks are added; unquoted lines are "missing" (Keep by default). */
export function buildScopePlan(lines: ProposalLineState[], quotes: QuoteSummary[], defaultProfitPct: number): ApplyPlan {
  const usable = quotes.filter(q => !q.isExpired);
  // Unpriced lines never price a material: "lowest" would always pick their $0.
  const candidates = (productId: number) => usable.flatMap(q => q.items.filter(i => i.hasPrice && i.productId === productId));
  const best = (list: QuoteOption[]) => list.reduce((a, b) => (b.unitPrice < a.unitPrice ? b : a));
  const note = (n: number) => (n > 1 ? `lowest of ${n} quotes` : undefined);
  const changes: PlannedChange[] = [];

  for (const line of lines) {
    const c = candidates(line.productId);
    if (c.length) {
      const after = lineFromOption(best(c), defaultProfitPct, line);
      changes.push({ kind: diffKind(line, after), before: line, after, note: note(c.length) });
    } else {
      changes.push({ kind: 'missing', before: line, after: null, remove: false });
    }
  }
  const have = new Set(lines.map(l => l.productId));
  const extra = [...new Set(usable.flatMap(q => q.items.filter(i => i.hasPrice).map(i => i.productId)))].filter(id => !have.has(id));
  for (const productId of extra) {
    const c = candidates(productId);
    changes.push({ kind: 'added', before: null, after: lineFromOption(best(c), defaultProfitPct), note: note(c.length) });
  }

  const label = usable.length === 1
    ? `${usable[0].quotationReference} from ${usable[0].vendorName}`
    : `${usable.length} quotes (${usable.map(q => q.quotationReference).join(', ')})`;
  return { from: 'scope', label, changes };
}

export const sellPrice = (l: ProposalLineState) => l.unitCost * (1 + l.profitPct / 100);
export const extendedSell = (l: ProposalLineState) => l.qty * sellPrice(l);

export function totals(lines: ProposalLineState[]): { cost: number; sell: number } {
  return {
    cost: lines.reduce((t, l) => t + l.qty * l.unitCost, 0),
    sell: lines.reduce((t, l) => t + extendedSell(l), 0),
  };
}

/** The scope's lines as they would be after the plan (used for the Compare totals, H1). */
export function simulate(lines: ProposalLineState[], plan: ApplyPlan): ProposalLineState[] {
  const out = lines.map(l => ({ ...l }));
  const removed = new Set<number | null>();
  for (const c of plan.changes) {
    if ((c.kind === 'changed' || c.kind === 'same') && c.before && c.after) {
      const target = out.find(l => l.id === c.before!.id);
      if (target) Object.assign(target, { qty: c.after.qty, unitCost: c.after.unitCost });
    } else if (c.kind === 'added' && c.after) {
      out.push({ ...c.after });
    } else if (c.kind === 'missing' && c.remove && c.before) {
      removed.add(c.before.id);
    }
  }
  return out.filter(l => !removed.has(l.id));
}

export const lineCostNow = (c: PlannedChange) => (c.before ? c.before.qty * c.before.unitCost : 0);
export const lineCostAfter = (c: PlannedChange) =>
  c.kind === 'missing' ? (c.remove ? 0 : lineCostNow(c)) : c.after ? c.after.qty * c.after.unitCost : 0;
export const lineDiff = (c: PlannedChange) => lineCostAfter(c) - lineCostNow(c);

export type ChangeLabelClass = 'dn' | 'up' | 'chg' | 'new' | 'miss' | 'rem' | 'same';

/** Row label on Compare (H2): Cheaper / Costs more / Updated / New item / Not quoted / Removed / Same. */
export function changeLabel(c: PlannedChange): { cls: ChangeLabelClass; text: string } {
  if (c.kind === 'added') return { cls: 'new', text: 'New item' };
  if (c.kind === 'same') return { cls: 'same', text: 'Same' };
  if (c.kind === 'missing') return c.remove ? { cls: 'rem', text: 'Removed' } : { cls: 'miss', text: 'Not quoted' };
  const d = lineDiff(c);
  return d < 0 ? { cls: 'dn', text: 'Cheaper' } : d > 0 ? { cls: 'up', text: 'Costs more' } : { cls: 'chg', text: 'Updated' };
}

const ORDER: Record<ChangeKind, number> = { changed: 0, added: 1, missing: 2, same: 3 };

/** Compare rows sorted changed → added → not quoted → same (H2). */
export function sortedChanges(plan: ApplyPlan): PlannedChange[] {
  return [...plan.changes].sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
}
